import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ArrowRight } from "lucide-react";
import { formatApiError } from "@/lib/api";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Accesso non riuscito");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen">
      {/* Left: form */}
      <div className="flex w-full flex-col justify-center px-6 py-12 sm:px-12 lg:w-[48%] lg:px-20">
        <div className="mx-auto w-full max-w-sm">
          <Logo variant="dark" className="mb-10" />
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Accedi al gestionale</h1>
          <p className="mt-2 text-sm text-slate-500">
            Piattaforma di gestione del ciclo di vita dei corsi di formazione.
          </p>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wide text-slate-500">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 h-11"
                data-testid="login-email"
                required
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-wide text-slate-500">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 h-11"
                data-testid="login-password"
                required
              />
            </div>
            {error && (
              <div className="rounded-lg bg-rose-50 border border-rose-200 px-3 py-2 text-sm text-rose-700" data-testid="login-error">
                {error}
              </div>
            )}
            <Button
              type="submit"
              disabled={loading}
              className="h-11 w-full bg-[#E11D48] hover:bg-[#BE123C] text-base"
              data-testid="login-submit"
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : (<>Accedi <ArrowRight className="ml-1.5 h-4 w-4" /></>)}
            </Button>
          </form>

          <p className="mt-8 text-xs text-slate-400">
            © {new Date().getFullYear()} INDAR Formazione & Sviluppo — CRM INDAR v1.0
          </p>
        </div>
      </div>

      {/* Right: brand panel */}
      <div className="relative hidden lg:block lg:w-[52%]">
        <img
          src="https://images.unsplash.com/photo-1524178232363-1fb2b075b655?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400"
          alt="Formazione"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/90 via-slate-900/70 to-[#E11D48]/50" />
        <div className="relative flex h-full flex-col justify-end p-14 text-white">
          <h2 className="font-display text-4xl font-bold leading-tight max-w-md">
            Un unico ambiente per l'intero ciclo di vita del corso.
          </h2>
          <p className="mt-4 max-w-md text-slate-200">
            Anagrafiche, pianificazione, iscrizioni, docenze, calendario, documenti, costi e rendicontazione — senza duplicare i dati.
          </p>
          <div className="mt-8 flex gap-8 text-sm">
            <div><div className="font-display text-2xl font-bold">FSE / GOL</div><div className="text-slate-300">Finanziati</div></div>
            <div><div className="font-display text-2xl font-bold">Fondi</div><div className="text-slate-300">Interprofessionali</div></div>
            <div><div className="font-display text-2xl font-bold">TREU</div><div className="text-slate-300">Tirocini</div></div>
          </div>
        </div>
      </div>
    </div>
  );
}
